import { Box, Menu, MenuItem, Portal } from "@mui/material";
import FeedingIcon from "common/icons/FeedingIcon";
import FertiliserIcon from "common/icons/FertiliserIcon";
import HarvestIcon from "common/icons/HarvestIcon";
import MeasurementIcon from "common/icons/MeasurementIcon";
import OtherEventIcon from "common/icons/OtherEventIcon";
import PurchaseIcon from "common/icons/PurchaseIcon";
import SeedingIcon from "common/icons/SeedingIcon";
import { useTranslation } from "common/locales";
import { useEffect, useState } from "react";

interface AddNewEventMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onEventTypeClick: (eventType: string) => void;
  addBottomOffset?: boolean; // If true, adds bottom margin for mobile bottom navigation
  animated?: boolean; // Enable fan animation effect
}

interface MenuItemConfig {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
}

const menuItems: MenuItemConfig[] = [
  {
    id: "reseed",
    labelKey: "addNewEvent.type.seeding",
    icon: <SeedingIcon htmlColor="#71717a" fontSize="small" />,
  },
  {
    id: "fertilizer",
    labelKey: "addNewEvent.type.fertilizer",
    icon: <FertiliserIcon htmlColor="#71717a" fontSize="small" />,
  },
  {
    id: "yield",
    labelKey: "addNewEvent.type.harvest",
    icon: <HarvestIcon htmlColor="#71717a" fontSize="small" />,
  },
  {
    id: "cover",
    labelKey: "addNewEvent.type.measurement",
    icon: <MeasurementIcon htmlColor="#71717a" fontSize="small" />,
  },
  {
    id: "feed",
    labelKey: "addNewEvent.type.feeding",
    icon: <FeedingIcon htmlColor="#71717a" fontSize="small" />,
  },
  {
    id: "feedPurchase",
    labelKey: "addNewEvent.type.purchase",
    icon: <PurchaseIcon htmlColor="#71717a" fontSize="small" />,
  },
  {
    id: "other",
    labelKey: "addNewEvent.type.other",
    icon: <OtherEventIcon htmlColor="#71717a" fontSize="small" />,
  },
];

export default function AddNewEventMenu({
  anchorEl,
  open,
  onClose,
  onEventTypeClick,
  addBottomOffset = false,
  animated = false,
}: AddNewEventMenuProps) {
  const [animationStarted, setAnimationStarted] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (open && animated) {
      const timer = setTimeout(() => setAnimationStarted(true), 10);
      return () => clearTimeout(timer);
    } else {
      setAnimationStarted(false);
    }
  }, [open, animated]);

  // Calculate bottom offset: BottomTabNavigation minHeight (70px) + safe area inset + extra spacing
  const bottomOffset = addBottomOffset
    ? "calc(15px + env(safe-area-inset-bottom) )"
    : undefined;

  // Fan effect: items fan out with slight vertical offset creating an arc
  const getFanOffset = (index: number) => {
    if (!animated) return 0;
    const middleIndex = (menuItems.length - 1) / 2;
    const distanceFromMiddle = index - middleIndex;
    return distanceFromMiddle * 3; // slight vertical offset in px
  };

  return (
    <>
      {/* Backdrop overlay - rendered via Portal to be above drawer (z-index 1200) and menu (z-index 1300) */}
      {animated && (
        <Portal>
          <Box
            onClick={onClose}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1350,
              opacity: open ? 1 : 0,
              pointerEvents: open ? "auto" : "none",
              transition: "opacity 0.15s ease",
            }}
          />
        </Portal>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        sx={{
          zIndex: 1400,
          ...(bottomOffset
            ? {
                "& .MuiPaper-root": {
                  marginBottom: bottomOffset,
                  maxHeight: `calc(100vh - ${bottomOffset} - 16px)`,
                  overflowY: "auto",
                  transform: `translateY(calc(-1 * ${bottomOffset})) !important`,
                },
              }
            : {}),
          "& .MuiBackdrop-root": {
            backgroundColor: "transparent",
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            ml: 1.5,
            overflow: "visible",
          },
        }}
        slotProps={{
          transition: {
            timeout: animated ? 150 : 225,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {menuItems.map((item, index) => (
            <MenuItem
              key={item.id}
              onClick={() => onEventTypeClick(item.id)}
              sx={{
                px: 2,
                py: 1.25,
                gap: 1.5,
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                color: "#09090b",
                backgroundColor: "#ffffff",
                border: "1px solid #e4e4e7",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                minHeight: "44px",
                "&:hover": {
                  backgroundColor: "#f4f4f5",
                },
                ...(animated && {
                  opacity: animationStarted ? 1 : 0,
                  transform: animationStarted
                    ? `translateY(${getFanOffset(index)}px)`
                    : `translateX(-15px) translateY(${getFanOffset(index)}px)`,
                  transition: `opacity 0.15s ease ${
                    index * 0.02
                  }s, transform 0.18s ease-out ${index * 0.02}s`,
                }),
              }}
            >
              {item.icon}
              {t(item.labelKey)}
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </>
  );
}
